import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { formatAudioTime } from './VerseLoopPlayer';
import VerseWaveform from './VerseWaveform';
import { analyzeAudioUrl } from './verseCut';

const DEFAULT_SPAN = 48;
const MIN_SPAN = 2;

const clampView = (start, span, duration) => {
  const limit = Number(duration) || 0;
  const nextSpan = Math.min(Math.max(span, MIN_SPAN), limit > 0 ? limit : span);
  const maxStart = Math.max(0, limit - nextSpan);
  return {
    span: nextSpan,
    start: Math.max(0, Math.min(maxStart, start)),
  };
};

const round1 = (value) => Math.round(Number(value) * 10) / 10;

/**
 * The full recording, with the saved beginning and ending already marked.
 * Drag the orange edges, or drag across the wave, to move that portion.
 */
export default function VersePortionEditor({
  audioUrl,
  originalUrl = '',
  startSec = 0,
  endSec = 0,
  durationSec = 0,
  onChange,
}) {
  const src = originalUrl || audioUrl || '';
  const audioRef = useRef(null);
  const previewEndRef = useRef(null);
  const waveBoxRef = useRef(null);
  const viewSpanRef = useRef(DEFAULT_SPAN);
  const viewStartRef = useRef(0);
  const durationRef = useRef(0);
  const [duration, setDuration] = useState(Number(durationSec) > Number(endSec) + 1 ? Number(durationSec) : 0);
  const [wave, setWave] = useState(null);
  const [waveStatus, setWaveStatus] = useState('Reading the full recording…');
  const [playhead, setPlayhead] = useState(Number(startSec) || 0);
  const [playing, setPlaying] = useState(false);
  const [viewStart, setViewStart] = useState(0);
  const [viewSpan, setViewSpan] = useState(DEFAULT_SPAN);
  viewSpanRef.current = viewSpan;
  viewStartRef.current = viewStart;
  durationRef.current = duration;

  const start = Number(startSec) || 0;
  const end = Number(endSec) > start ? Number(endSec) : start + 0.4;

  useEffect(() => {
    const node = audioRef.current;
    if (!node) return undefined;
    const onTime = () => {
      const current = node.currentTime || 0;
      setPlayhead(current);
      if (previewEndRef.current != null && current >= previewEndRef.current) {
        previewEndRef.current = null;
        node.pause();
      }
    };
    const onMeta = () => {
      if (Number.isFinite(node.duration) && node.duration > 0) setDuration(node.duration);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    node.addEventListener('timeupdate', onTime);
    node.addEventListener('loadedmetadata', onMeta);
    node.addEventListener('play', onPlay);
    node.addEventListener('pause', onPause);
    return () => {
      node.removeEventListener('timeupdate', onTime);
      node.removeEventListener('loadedmetadata', onMeta);
      node.removeEventListener('play', onPlay);
      node.removeEventListener('pause', onPause);
    };
  }, [src]);

  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    setWaveStatus('Reading the full recording…');
    analyzeAudioUrl(src, {
      durationHint: durationRef.current || Number(durationSec) || 0,
      onUpdate: (partial) => {
        if (cancelled || !partial?.peaks?.length) return;
        setWave(partial);
        if (Number(partial.duration) > 0) {
          setDuration((current) => current || partial.duration);
        }
        setWaveStatus(partial.partial ? 'Reading the full recording…' : '');
      },
    }).then((result) => {
      if (cancelled || !result) return;
      setWave(result);
      if (Number(result.duration) > 0) setDuration((current) => current || result.duration);
      setWaveStatus('');
    }).catch(() => {
      if (!cancelled) setWaveStatus('The waveform could not be drawn. The times below still save.');
    });
    return () => { cancelled = true; };
  }, [src, durationSec]);

  const framedRef = useRef(false);

  useEffect(() => {
    if (!duration || framedRef.current) return;
    framedRef.current = true;
    const length = Math.max(1, end - start);
    const span = Math.min(duration, Math.max(DEFAULT_SPAN, length * 2.4));
    const next = clampView(start - (span - length) / 2, span, duration);
    setViewSpan(next.span);
    setViewStart(next.start);
  }, [duration, start, end]);

  useEffect(() => {
    const node = waveBoxRef.current;
    if (!node) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const view = viewStartRef.current;
      const span = viewSpanRef.current;
      const limit = durationRef.current;
      const rect = node.getBoundingClientRect();
      const ratio = rect.width ? (event.clientX - rect.left) / rect.width : 0.5;
      const focus = view + ratio * Math.min(span, limit || span);
      const factor = event.deltaY < 0 ? 0.8 : 1.25;
      const next = clampView(focus - span * factor * ratio, span * factor, limit);
      setViewSpan(next.span);
      setViewStart(next.start);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [wave?.peaks?.length, duration]);

  const apply = (nextStart, nextEnd) => {
    const limit = Number(duration) || 0;
    let from = Math.max(0, Number(nextStart) || 0);
    let to = Math.max(from + 0.4, Number(nextEnd) || 0);
    if (limit > 0) {
      to = Math.min(limit, to);
      from = Math.min(from, Math.max(0, to - 0.4));
    }
    onChange?.({ startSec: round1(from), endSec: round1(to) });
  };

  const zoomWave = (span) => {
    const focus = (start + end) / 2;
    const next = clampView(focus - span / 2, span, duration);
    setViewSpan(next.span);
    setViewStart(next.start);
  };

  const preview = async () => {
    const node = audioRef.current;
    if (!node) return;
    if (playing) {
      node.pause();
      return;
    }
    previewEndRef.current = end;
    try { node.currentTime = start; } catch { /* ignore */ }
    setPlayhead(start);
    try { await node.play(); } catch { /* The browser may ask for another tap. */ }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={700}>Full recording</Typography>
      <Typography variant="caption" color="text.secondary">
        The orange area is the saved clip, {formatAudioTime(start)} – {formatAudioTime(end)}. Drag either orange edge, or drag across the wave, to move the beginning or the ending. Zoom out to see the whole file.
      </Typography>
      <audio ref={audioRef} src={src} preload="metadata" />
      {waveStatus ? (
        <Typography variant="caption" color="text.secondary">{waveStatus}</Typography>
      ) : null}
      {wave?.peaks?.length && duration ? (
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
            <Button size="small" variant="outlined" onClick={() => zoomWave(viewSpan * 2)} disabled={viewSpan >= duration - 0.2}>
              Zoom out
            </Button>
            <Button size="small" variant="outlined" onClick={() => zoomWave(viewSpan / 2)} disabled={viewSpan <= MIN_SPAN + 0.05}>
              Zoom in
            </Button>
            <Button size="small" variant="outlined" onClick={() => zoomWave(Math.max(MIN_SPAN, (end - start) * 1.8))}>
              Zoom selection
            </Button>
            <Button size="small" variant="contained" onClick={preview}>
              {playing ? 'Pause' : 'Play selection'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {viewSpan >= duration - 0.2 ? 'Whole recording' : `${Math.max(1, Math.round(viewSpan))}s on screen`}
            </Typography>
          </Stack>
          <Box ref={waveBoxRef}>
            <VerseWaveform
              peaks={wave.peaks}
              duration={duration}
              viewStart={viewStart}
              viewSpan={Math.min(viewSpan, duration)}
              playhead={playhead}
              startSec={start}
              endSec={end}
              onSeek={(sec) => {
                const node = audioRef.current;
                previewEndRef.current = null;
                if (node) {
                  try { node.currentTime = sec; } catch { /* ignore */ }
                }
                setPlayhead(sec);
              }}
              onSelection={(from, to) => apply(from, to)}
              onViewChange={(nextStart, nextSpan) => {
                const next = clampView(nextStart, nextSpan, duration);
                setViewSpan(next.span);
                setViewStart(next.start);
              }}
            />
          </Box>
          {duration > viewSpan + 0.5 ? (
            <Box
              component="input"
              type="range"
              min={0}
              max={Math.max(0, duration - viewSpan)}
              step={0.1}
              value={Math.min(viewStart, Math.max(0, duration - viewSpan))}
              aria-label="Scroll the recording"
              onChange={(event) => setViewStart(Number(event.target.value) || 0)}
              sx={{ width: '100%' }}
            />
          ) : null}
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Button size="small" variant="outlined" onClick={() => apply(start - 5, end)}>Beginning −5s</Button>
            <Button size="small" variant="outlined" onClick={() => apply(start - 1, end)}>Beginning −1s</Button>
            <Button size="small" variant="outlined" onClick={() => apply(start + 1, end)}>Beginning +1s</Button>
            <Button size="small" variant="outlined" onClick={() => apply(start + 5, end)}>Beginning +5s</Button>
            <Button size="small" variant="outlined" onClick={() => apply(start, end - 5)}>Ending −5s</Button>
            <Button size="small" variant="outlined" onClick={() => apply(start, end - 1)}>Ending −1s</Button>
            <Button size="small" variant="outlined" onClick={() => apply(start, end + 1)}>Ending +1s</Button>
            <Button size="small" variant="outlined" onClick={() => apply(start, end + 5)}>Ending +5s</Button>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}
